import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ConsultLinkTracker } from "./components/ConsultLinkTracker";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { PermissionProvider } from "./context/PermissionContext";
import { WishlistProvider } from "./contexts/WishlistContext";

// Pages
import { HomePage } from "./pages/HomePage";
import { AboutPage } from "./pages/AboutPage";
import { ProductsPage } from "./pages/ProductsPage";
import { BlindsPage } from "./pages/BlindsPage";
import { SegmentPage } from "./pages/SegmentPage";
import { PrivacyPage, TermsPage } from "./pages/LegalPages";
import ServiceAreaPage from "./pages/ServiceAreaPage";
import MeasuringInstallationPage from "./pages/MeasuringInstallationPage";
import ShadeFabricGuidePage from "./pages/ShadeFabricGuidePage";
import WarrantyServicePage from "./pages/WarrantyServicePage";
import { RouteSeo } from "./seo/RouteSeo";
import { ScrollToTop } from "./components/ScrollToTop";
import { ProductDetailPage } from "./pages/ProductDetailPage";
import { MotorizedDrapesPage } from "./pages/MotorizedDrapesPage";
import { GalleryPage } from "./pages/GalleryPage";
import { FabricsPage } from "./pages/FabricsPage";
import { TradePage } from "./pages/TradePage";
import { MotorizationPage } from "./pages/MotorizationPage";
import { SmartConnectivityPage } from "./pages/SmartConnectivityPage";
import { PlatformDetailPage } from "./pages/PlatformDetailPage";
import { MatterPage } from "./pages/MatterPage";
import { HubsPage } from "./pages/HubsPage";
import { BuiltInMotorTechnologyPage } from "./pages/BuiltInMotorTechnologyPage";
import { WorksWithPage } from "./pages/WorksWithPage";
import { FAQPage } from "./pages/FAQPage";
import { ContactPage } from "./pages/ContactPage";
import { QuotePage } from "./pages/QuotePage";
import { BlogPage, BlogPostPage } from "./pages/BlogPage";
import SearchPage from "./pages/SearchPage";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "./pages/AuthPages";
import { VerifyEmailPage, VerifyEmailPendingPage } from "./pages/VerifyEmailPage";
import { CheckoutPage } from "./pages/CheckoutPage";
import { QuotesPage } from "./pages/SavedPages";
import SavedConfigsPage from "./pages/SavedConfigsPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import { AdminPage } from "./pages/AdminPage";
import ShowroomFitPage from "./pages/ShowroomFitPage";
import OpennessTestPage from "./pages/OpennessTestPage";
import { ConsultationPage } from "./pages/ConsultationPage";
import { ST120FormPage } from "./pages/ST120FormPage";
import { DashboardPage } from "./pages/DashboardPage";
import DynamicPage from "./pages/DynamicPage";
import { NEW_BUILDER_PATHS } from "./data/productNav";
import BuilderPreviewPage from "./pages/BuilderPreviewPage";
import { CmsSwitch } from "./components/website/CmsSwitch";
import { SiteProvider } from "./hooks/useSiteConfig";
import { PartnersPage } from "./pages/PartnersPage";
import { PartnerDetailPage } from "./pages/PartnerDetailPage";
import { AuthCallback } from "./pages/AuthCallback";
import { InvoicePaymentPage } from "./pages/InvoicePaymentPage";
import { CompareProductsPage } from "./pages/CompareProductsPage";
import FeedbackPage from "./pages/FeedbackPage";
import TrackingPage from "./pages/TrackingPage";
import CustomerPortalPage from "./pages/CustomerPortalPage";
import LegacyQuoteLink from "./pages/LegacyQuoteLink";
import MyFavoritesPage from "./pages/MyFavoritesPage";
import RequestSamplePage from "./pages/RequestSamplePage";
import TrackSamplesPage from "./pages/TrackSamplesPage";
import MyOrdersPage from "./pages/MyOrdersPage";
import SharedComparisonPage from "./pages/SharedComparisonPage";
import PreviewSharePage from "./pages/PreviewSharePage";
import OrderTrackingPage from "./pages/OrderTrackingPage";
import InstallerMobilePage from "./pages/InstallerMobilePage";

// Global Components
import { LiveChat } from "./components/LiveChat";
import { WhatsAppButton } from "./components/WhatsAppButton";
import { AIChatWidget } from "./components/AIChatWidget";
import { ChatAgentSelector } from "./components/ChatAgentSelector";

import { BreadcrumbSchema } from "./components/website/BreadcrumbSchema";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SiteProvider>
        <AuthProvider>
          <WishlistProvider>
          <PermissionProvider>
            <CartProvider>
              <RouteSeo />
              <ScrollToTop />
              <ConsultLinkTracker />
              <BreadcrumbSchema />
              <Routes>
                {/* Main Pages */}
              <Route path="/" element={<CmsSwitch path="/"><HomePage /></CmsSwitch>} />
              <Route path="/about" element={<CmsSwitch path="/about"><AboutPage /></CmsSwitch>} />
              
              {/* Products - Shades */}
              <Route path="/products" element={<CmsSwitch path="/products"><ProductsPage /></CmsSwitch>} />
              <Route path="/products/blinds" element={<BlindsPage />} />
              <Route path="/products/residential" element={<SegmentPage segment="residential" />} />
              <Route path="/products/commercial" element={<SegmentPage segment="commercial" />} />
              <Route path="/products/architectural" element={<SegmentPage segment="architectural" />} />
              <Route path="/products/shades" element={<ProductsPage />} />
              <Route path="/products/roller-shades" element={<ProductDetailPage />} />
              <Route path="/products/solar-shades" element={<ProductDetailPage />} />
              <Route path="/products/roman-shades" element={<ProductDetailPage />} />
              <Route path="/products/cellular-shades" element={<ProductDetailPage />} />
              <Route path="/products/zebra-shades" element={<ProductDetailPage />} />
              
              {/* Products - Drapery */}
              <Route path="/products/drapery" element={<ProductsPage />} />
              <Route path="/products/motorized-drapes" element={<MotorizedDrapesPage />} />
              <Route path="/products/ripple-fold-drapes" element={<ProductDetailPage />} />
              <Route path="/products/sheer-drapes" element={<ProductDetailPage />} />
              <Route path="/products/pinch-pleat-drapes" element={<ProductDetailPage />} />
              <Route path="/products/grommet-drapes" element={<ProductDetailPage />} />
              <Route path="/products/side-panels" element={<ProductDetailPage />} />
              
              {/* Products - Motorization */}
              <Route path="/products/motorization" element={<Navigate to="/motorization" replace />} />
              <Route path="/products/motorized-shades" element={<ProductDetailPage />} />
              <Route path="/products/motorized-blinds" element={<SegmentPage segment="motorized-blinds" />} />
              <Route path="/products/motorized-skylights" element={<SegmentPage segment="motorized-skylights" />} />
              <Route path="/products/smart-home-integration" element={<SegmentPage segment="smart-home-integration" />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/service-area" element={<ServiceAreaPage />} />
              <Route path="/measuring-installation" element={<MeasuringInstallationPage />} />
              <Route path="/blackout-vs-light-filtering-vs-solar-shades" element={<ShadeFabricGuidePage />} />
              <Route path="/warranty-service" element={<WarrantyServicePage />} />
              {NEW_BUILDER_PATHS.map((p) => <Route key={p} path={p} element={<DynamicPage />} />)}
              <Route path="/products/:productType" element={<ProductDetailPage />} />
              
              <Route path="/gallery" element={<CmsSwitch path="/gallery"><GalleryPage /></CmsSwitch>} />
              <Route path="/fabrics" element={<FabricsPage />} />
              <Route path="/trade" element={<CmsSwitch path="/trade"><TradePage /></CmsSwitch>} />
              
              {/* Partners */}
              <Route path="/partners" element={<CmsSwitch path="/partners"><PartnersPage /></CmsSwitch>} />
              <Route path="/partners/crestron" element={<Navigate to="/partners" replace />} />
              <Route path="/partners/:partnerId" element={<PartnerDetailPage />} />
              
              {/* Motorization & Smart Connectivity (legacy routes still work) */}
              <Route path="/motorization" element={<CmsSwitch path="/motorization"><MotorizationPage /></CmsSwitch>} />
              <Route path="/motorization/roller-motor" element={<ProductDetailPage />} />
              <Route path="/motorization/drapery-motor" element={<ProductDetailPage />} />
              <Route path="/motorization/roman-shade-motor" element={<ProductDetailPage />} />
              <Route path="/motorization/smart-connectivity" element={<SmartConnectivityPage />} />
              <Route path="/motorization/smart-connectivity/works-with-motionblinds" element={<WorksWithPage />} />
              <Route path="/motorization/smart-connectivity/matter" element={<MatterPage />} />
              <Route path="/motorization/smart-connectivity/hubs" element={<HubsPage />} />
              <Route path="/motorization/smart-connectivity/built-in-motor-technology" element={<BuiltInMotorTechnologyPage />} />
              <Route path="/motorization/smart-connectivity/:platformId" element={<PlatformDetailPage />} />
              
              {/* Smart Connectivity direct routes */}
              <Route path="/smart-connectivity" element={<Navigate to="/motorization/smart-connectivity" replace />} />
              <Route path="/matter" element={<Navigate to="/motorization/smart-connectivity/matter" replace />} />
              <Route path="/built-in-motor-technology" element={<Navigate to="/motorization/smart-connectivity/built-in-motor-technology" replace />} />
              
              <Route path="/faq" element={<CmsSwitch path="/faq"><FAQPage /></CmsSwitch>} />
              <Route path="/contact" element={<CmsSwitch path="/contact"><ContactPage /></CmsSwitch>} />
              <Route path="/quote" element={<QuotePage />} />
              <Route path="/consultation" element={<ConsultationPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              
              {/* Auth & User Pages */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/verify-email-pending" element={<VerifyEmailPendingPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
              <Route path="/saved" element={<SavedConfigsPage />} />
              <Route path="/saved-configs" element={<SavedConfigsPage />} />
              <Route path="/my-quotes" element={<QuotesPage />} />
              <Route path="/my-favorites" element={<MyFavoritesPage />} />
              <Route path="/my-orders" element={<MyOrdersPage />} />
              <Route path="/request-samples" element={<RequestSamplePage />} />
              <Route path="/track-samples" element={<TrackSamplesPage />} />
              <Route path="/compare-fabrics" element={<SharedComparisonPage />} />
              <Route path="/preview/:token" element={<PreviewSharePage />} />
              <Route path="/st120" element={<ST120FormPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/pay/invoice/:invoiceId" element={<InvoicePaymentPage />} />
              
              {/* Product Comparison with shareable URL */}
              <Route path="/products/compare" element={<CompareProductsPage />} />
              
              {/* Customer Feedback Survey */}
              <Route path="/feedback/:token" element={<FeedbackPage />} />
              
              {/* Live Tracking for Customers */}
              <Route path="/track/:token" element={<TrackingPage />} />
              
              {/* Order Tracking (Public) */}
              <Route path="/track/order/:code" element={<OrderTrackingPage />} />
              
              {/* Installer Mobile Portal */}
              <Route path="/installer" element={<InstallerMobilePage />} />
              
              {/* Customer Portal */}
              <Route path="/customer-portal" element={<CustomerPortalPage />} />
              <Route path="/quote/:quoteId" element={<LegacyQuoteLink />} />
              
              {/* Admin */}
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/dev/showroom-fit" element={<ShowroomFitPage />} />
              <Route path="/dev/openness-test" element={<OpennessTestPage />} />
              <Route path="/admin/preview-frame" element={<BuilderPreviewPage />} />
              
              {/* Dynamic CMS Pages - Must be last to act as catch-all */}
              <Route path="*" element={<DynamicPage />} />
            </Routes>
            
            {/* Global Floating Components */}
            {!window.location.pathname.startsWith('/admin/preview-frame') && <><WhatsAppButton /><ChatAgentSelector /></>}
            </CartProvider>
          </PermissionProvider>
          </WishlistProvider>
        </AuthProvider>
        </SiteProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
